import { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { s3upload } from '../../utils/filesystem/fileUpload';
import { resizeImage } from '../../utils/images/imageResizer';
import { useAppContext } from '../../utils/context';
import parseValidationErrors from '../../utils/errors/parseValidationErrors';

export default function useModifyMutation ({
	modifyMutation,
	id,
	filePath = 'product', // By default product
	data, // Used for optimistic UI
	optimisticResponse,
	optimisticResponseCondition = () => true,
	skipResizingImages,
}) {
	const [mutationFunction, mutationStatus] = useMutation(modifyMutation);
	const { getServerEndpoint } = useAppContext();

	const modifyItem = async (values) => {
		const {
			image, smallImage, mobileImage, mobileBannerImage, ...otherValues
		} = values;

		const uploadSettings = { skipResizingImages, filePath, getServerEndpoint };

		const imageUrls = await getImageURls(image, smallImage, mobileImage, mobileBannerImage, uploadSettings);

		return mutationFunction({
			variables: {
				input: {
					id,
					...imageUrls,
					...otherValues,
				},
			},
			optimisticResponse: optimisticResponse && (optimisticResponseCondition(values) ? optimisticResponse(values, data) : undefined),
		});
	};

	const validationErrors = useMemo(() => parseValidationErrors(mutationStatus.error), [mutationStatus.error]);

	return {
		modifyItem,
		modifyItemStatus: { ...mutationStatus, validationErrors },
	};
}

const getImageUrl = async (image, uploadSettings) => {
	const { skipResizingImages, filePath, getServerEndpoint } = uploadSettings;
	// resize the image if needed
	const processedFile = skipResizingImages ? image : await resizeImage({ image, maxWidth: 500, maxHeight: 500 });

	// upload to S3 and get the url
	return s3upload(processedFile, null, `${image ? 'images' : 'files'}/${filePath}s/`, getServerEndpoint);
};

const getImageURls = async (image, smallImage, mobileImage, mobileBannerImage, uploadSettings) => {

	const imageUrl = (window && image) ? await getImageUrl(image, uploadSettings) : null;
	const smallImageUrl = (window && mobileImage) ? await getImageUrl(smallImage, uploadSettings) : null;
	const mobileImageUrl = (window && mobileImage) ? await getImageUrl(mobileImage, uploadSettings) : null;

	return {
		...(imageUrl ? { imageUrl } : {}),
		...(smallImageUrl ? { smallImageUrl } : {}),
		...(mobileImageUrl ? { mobileImageUrl } : {}),
	};
};
